import * as React from 'react';
import * as L from "leaflet";
import 'leaflet/dist/leaflet.css';

import "./analytics-map-control.scss";

import {cs}        from "@Common/react/chain-services";
import {fragments} from "@Common/react/fragments";
import {keyed}     from "@Common/react/keyed";
import {UseState}  from "@Common/react/use-state";
import {Watch}     from '@Common/react/watch';
import {equalDeep} from '@Common/utils/objects';

import {LeafletMap} from "../../mailing-ui/create-mailing-list-modal/types/common/leaflet-map/leaflet-map";
import {cx}         from 'emotion';

export const AnalyticsMapControl = ({
    center,
    hoveredContent,
    shapes=undefined,
    pills=undefined,
    viewSize,
}) => cs(
    ["cursorPosition", (_, next) => UseState({
        next,
    })],
    ["circleRefs", (_, next) => UseState({initValue: [], next})],
    ["leafletMap", ({circleRefs, cursorPosition}, next) => LeafletMap({
        center,
        onMouseMove: (e) => cursorPosition.onChange({pos: getELoc(e.originalEvent), latlng: e.latlng}),
        onZoomEnd: ({map, prevZoom, renderedElements}) => {
            prevZoom.onChange(map.getZoom());
            renderItems({circleRefs, map, renderedElements});
        },
        renderItems: ({map, renderedElements}) => renderItems({circleRefs, map, renderedElements}),
        isSidebarClosed: true,
        xdQaPrefix: "New Homeowners",
        next,
    })],
    ({leafletMap, cursorPosition, circleRefs}) => fragments(
        (
            <div className="analytics-map-control-45t">
                <div className={cx("map", viewSize)} ref={leafletMap.ref}/>
                {hoveredContent && cursorPosition.value && (
                    InfoBox({
                        hoveredContent,
                        position: cursorPosition.value,
                    })
                )}
            </div>
        ),
        shapes?.map((shape) => cs(
            keyed(shape.elementKey),
            () => (
                leafletMap.renderPolygon({
                    shape,
                    onClick: shape.onClick,
                    onMouseOver: shape.onMouseOver,
                    onMouseOut : shape.onMouseOut,
                })
            )
        )),
        pills?.map((pill) => cs(
            keyed(pill.elementKey),
            () => (
                leafletMap.createCustomIcon({
                    elementKey: pill.elementKey,
                    coords: pill.position,
                    icon: ({
                        className: 'pill-icon',
                        html: `<div class="${cx("pill", {"no-record": !pill.count})}" xd-qa-name="New Homeowners ${pill.elementKey} Qty" xd-qa-type="label">${pill.count}</div>`
                    }),
                    onMouseOver: pill.onMouseOver,
                    onMouseOut: pill.onMouseOut,
                })
            )
        )),
        Watch({
            value: shapes,
            compare: (s1, s2) => equalDeep(s1?.map(s => s.elementKey), s2?.map(s => s.elementKey)),
            onChanged: () => {
                leafletMap.cleanMap({
                    elements: [...(shapes ?? []), ...(pills ?? [])],
                });
                if (circleRefs.value.length) {
                    leafletMap.cleanItems(circleRefs.value.map((circle) => ({get: () => circle})));
                    circleRefs.onChange([]);
                }
            },
        }),
        Watch({
            value: viewSize,
            onChanged: () => leafletMap.invalidateSize(),
        }),
    )
);

const getELoc = (e) => {
    let x = e.clientX;
    let y = e.clientY;

    return {x, y};
};

const InfoBox = ({hoveredContent, position}) => {
    return (
        <div style={{position:"fixed", top: position.pos.y + 10, left: position.pos.x + 10}}>
            {hoveredContent}
        </div>
    )
};

const renderItems = ({circleRefs, map, renderedElements}) => {
    const zoom = map.getZoom();

    const pillElements = renderedElements?.filter(({type}) => type === 'icon');
    if (zoom >= 10) {
        pillElements?.forEach(({ref}) => !map.hasLayer(ref.get()) && ref.get().addTo(map));
    } else {
        pillElements?.forEach(({ref}) => map.hasLayer(ref.get()) && map.removeLayer(ref.get()));
    }

    const polygonElements = renderedElements?.filter(({type}) => type === 'polygon');
    if (zoom <= 6) {
        let newCircles = [];
        const metersPerPixel = map.distance(map.containerPointToLatLng([0, 0]), map.containerPointToLatLng([1, 0]));
        polygonElements?.map(({ref: polygonRef}) => {
            const polygonLatLngs = flattenLatLngs(polygonRef.get().getLatLngs());
            const oldCircle = newCircles.find((oldCircle) => {
                const circleCenter = oldCircle.getLatLng();
                const circleRadius = oldCircle.getRadius() * metersPerPixel;
                let allPointsWithinCircle = true;
                polygonLatLngs.forEach(point => {
                    const distance = point.distanceTo(circleCenter);
                    if (distance > circleRadius) {
                        allPointsWithinCircle = false;
                    }
                });
                return allPointsWithinCircle
            });

            if (!oldCircle) {
                let totalLat = 0;
                let totalLng = 0;
                polygonLatLngs.forEach(({lat, lng}) => {
                    totalLat += lat;
                    totalLng += lng;
                });
                const centerLat = totalLat / polygonLatLngs.length;
                const centerLng = totalLng / polygonLatLngs.length;

                const circle = L.circleMarker([centerLat, centerLng], {
                    radius: 50,
                    color: polygonRef.get().options.color,
                    fillOpacity: 0.1,
                });
                newCircles = [...newCircles, circle];
            } else {
                const circleColor = oldCircle.options.color;
                const polygonColor = polygonRef.get().options.color;
                if (
                    circleColor !== polygonColor &&
                    polygonColor === "#249AF3"
                ) {
                    oldCircle.setStyle({color: polygonColor});
                }
            }
        });
        newCircles.map((circle) => {
            circle.addTo(map);
        });
        circleRefs.value.forEach((circle) => map.removeLayer(circle));
        circleRefs.onChange(newCircles);
    } else if (circleRefs.value.length && zoom > 6) {
        circleRefs.value.forEach((circle) => map.removeLayer(circle));
        circleRefs.onChange([]);
    }
}

function flattenLatLngs(arr) {
    return arr.reduce((acc, val) => {
        if (Array.isArray(val)) {
            return acc.concat(flattenLatLngs(val));
        } else {
            return acc.concat(val);
        }
    }, []);
}

function isPointInsidePolygon(point, polygon) {
    var x = point.lat, y = point.lng;
    var inside = false;

    for (var i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        var xi = polygon[i].lat, yi = polygon[i].lng;
        var xj = polygon[j].lat, yj = polygon[j].lng;

        var intersect = ((yi > y) != (yj > y)) &&
            (x < ((xj - xi) * (y - yi) / (yj - yi) + xi));
        if (intersect) inside = !inside;
    }

    return inside;
}